/**
 * GraphQL Relationships Component
 *
 * Displays related objects from GraphQL response data.
 * Extracts nested objects and arrays from the response and shows them as clickable links.
 */

import { IdLink } from './IdLink';
import { isGlobalId, decodeGlobalId } from '@206mp/shared/utils/globalObjectId';

export interface GraphQLRelationshipsProps {
  /** The full object data from GraphQL response */
  objectData: Record<string, any>;
  /** Whether data is still loading */
  isLoading?: boolean;
}

interface RelatedObject {
  fieldName: string;
  type: string;
  globalId: string;
  displayName?: string;
}

interface RelatedCollection {
  fieldName: string;
  type: string;
  items: Array<{ globalId: string; displayName?: string }>;
  totalCount?: number;
}

/**
 * Extract related objects and collections from GraphQL response
 */
function extractRelationships(data: Record<string, any>): {
  relatedObjects: RelatedObject[];
  relatedCollections: RelatedCollection[];
} {
  const relatedObjects: RelatedObject[] = [];
  const relatedCollections: RelatedCollection[] = [];

  for (const [key, value] of Object.entries(data)) {
    // Skip non-object values and metadata fields
    if (!value || typeof value !== 'object' || key === '__typename') {
      continue;
    }

    // Check if it's a single related object (has id field)
    if (value.id && typeof value.id === 'string' && isGlobalId(value.id)) {
      const decoded = decodeGlobalId(value.id);
      if (!('error' in decoded)) {
        relatedObjects.push({
          fieldName: key,
          type: decoded.type,
          globalId: value.id,
          displayName: value.username || value.title || value.name || value.variantType,
        });
      }
      continue;
    }

    // Check if it's a connection type (has items array)
    if (value.items && Array.isArray(value.items)) {
      const items = value.items
        .filter((item: any) => item?.id && isGlobalId(item.id))
        .map((item: any) => {
          const decoded = decodeGlobalId(item.id);
          return {
            globalId: item.id,
            displayName: item.username || item.title || item.name || item.variantType,
            type: !('error' in decoded) ? decoded.type : 'unknown',
          };
        });

      if (items.length > 0) {
        relatedCollections.push({
          fieldName: key,
          type: items[0].type,
          items,
          totalCount: value.totalCount,
        });
      }
      continue;
    }

    // Check if it's a plain array of related objects
    if (Array.isArray(value)) {
      const items = value
        .filter((item: any) => item?.id && isGlobalId(item.id))
        .map((item: any) => {
          const decoded = decodeGlobalId(item.id);
          return {
            globalId: item.id,
            displayName: item.username || item.title || item.name || item.variantType,
            type: !('error' in decoded) ? decoded.type : 'unknown',
          };
        });

      if (items.length > 0) {
        relatedCollections.push({
          fieldName: key,
          type: items[0].type,
          items,
        });
      }
    }
  }

  return { relatedObjects, relatedCollections };
}

export function GraphQLRelationships({ objectData, isLoading }: GraphQLRelationshipsProps) {
  if (isLoading) {
    return (
      <div className="p-4 bg-surface-1 border border-border rounded-lg">
        <p className="text-muted text-sm">Loading relationships...</p>
      </div>
    );
  }

  const { relatedObjects, relatedCollections } = extractRelationships(objectData);

  if (relatedObjects.length === 0 && relatedCollections.length === 0) {
    return (
      <div className="p-4 bg-surface-1 border border-border rounded-lg">
        <p className="text-muted text-sm">
          No related objects found in response.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Single Related Objects */}
      {relatedObjects.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-muted uppercase tracking-wide">
            Related Objects
          </h3>
          {relatedObjects.map((rel) => (
            <div
              key={rel.fieldName}
              className="p-3 bg-surface-1 border border-border rounded-lg hover:bg-surface-2 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs text-muted">{rel.fieldName}</span>
                  <div className="flex items-center gap-2 mt-1">
                    <IdLink globalId={rel.globalId} typeName={rel.type} />
                    {rel.displayName && (
                      <span className="text-sm text-muted">
                        ({rel.displayName})
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-xs font-mono text-muted bg-surface-2 px-2 py-1 rounded">
                  {rel.type}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Related Collections */}
      {relatedCollections.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-muted uppercase tracking-wide">
            Related Collections
          </h3>
          {relatedCollections.map((rel) => (
            <div
              key={rel.fieldName}
              className="p-3 bg-surface-1 border border-border rounded-lg"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">
                  {rel.fieldName}
                </span>
                <span className="text-xs text-muted">
                  {rel.totalCount !== undefined
                    ? `${rel.items.length} of ${rel.totalCount}`
                    : `${rel.items.length} items`}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {rel.items.slice(0, 10).map((item) => (
                  <IdLink
                    key={item.globalId}
                    globalId={item.globalId}
                    typeName={rel.type}
                  />
                ))}
                {rel.items.length > 10 && (
                  <span className="text-xs text-muted self-center">
                    +{rel.items.length - 10} more
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
